/*  Компонент - задел на будущее.
Не протестирован. */
import { forwardRef, useContext, useEffect } from 'react';
import isEmpty from 'lodash/isEmpty';
import { FastField, getIn } from 'formik';
import type { FieldProps } from 'formik';

import { FormContext, isRequiredField } from '../utils';
import SimpleTimeInput from './SimpleTimeInput';
import type { SimpleTimeInputProps } from './SimpleTimeInput';

/**
 * Собственные пропсы Formik-обёртки инпута времени (по образцу StringInputOwnProps):
 * то, что обёртка обрабатывает сама, а НЕ пробрасывает в SimpleTimeInput как есть.
 */
export interface TimeInputOwnProps {
  /** Ручной признак обязательности; иначе автодетект по validationSchema (isRequiredField). */
  required?: boolean;
  /** Yup-схема формы; берётся из FormContext, если не передана явно. */
  validationSchema?: any;
  /** Колбэк вызывается, когда поле переходит в состояние ошибки валидации. */
  onValidateFail?: (error: unknown) => void;
  /** Кастомный предикат для FastField shouldUpdate. */
  shouldComponentUpdate?: (next: any, prev: any) => boolean;
}

/**
 * Пропсы Formik-обёртки инпута времени (TimeInput).
 *
 * `field`/`meta`/`form` инжектируются render-prop'ом `<FastField>` во внутренний
 * компонент и НЕ входят в этот тип — внешний потребитель их не передаёт.
 */
export type TimeInputProps = Omit<SimpleTimeInputProps, 'required'> & TimeInputOwnProps;

/** Внутренние пропсы: {@link TimeInputProps} + `field`/`meta`/`form` из formik `<FastField>`. */
type TimeInputInnerProps = TimeInputProps & FieldProps;

const TimeInput = forwardRef<HTMLElement, TimeInputInnerProps>((props, ref) => {
  const {
    field,
    form,
    meta,
    value,
    required,
    validationSchema,
    onValidateFail,
    ...rest
  } = props;

  const {
    submitCount,
    setFieldValue,
    setFieldTouched,
  } = form;

  const hasError = !isEmpty(meta.error)
    && ((meta.touched && (field.value !== undefined)) || (submitCount > 0));

  useEffect(() => {
    if (value && (value !== field.value)) {
      setFieldValue(field.name, value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (!meta.touched) {
      setFieldTouched(field.name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.value]);

  useEffect(() => {
    if (hasError && (typeof onValidateFail === 'function')) {
      onValidateFail(meta.error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasError]);

  return (
    <SimpleTimeInput
      { ...field }
      { ...rest }
      ref={ ref }
      value={ (field.value !== null && field.value !== undefined) ? field.value : '' }
      error={ hasError ? meta.error : null }
      required={ required || isRequiredField(validationSchema, field.name) }
      onChange={ (e, newValue) => { setFieldValue(field.name, newValue); } }
    />
  );
});

const TimeInputWrapper = forwardRef<HTMLElement, TimeInputProps>((props, ref) => {
  const { shouldComponentUpdate, as, ...rest } = props;
  // FormContext создаётся в utils.js (createContext() без дженерика); при allowJs:false
  // useContext возвращает unknown — приводим к ожидаемой форме контекста формы.
  const { validationSchema } = useContext(FormContext) as { validationSchema?: any };

  const defaultShouldComponentUpdate = (nextProps: any, currentProps: any): boolean => {
    return nextProps.name !== currentProps.name
      || nextProps.required !== currentProps.required
      || nextProps.disabled !== currentProps.disabled
      || nextProps.readOnly !== currentProps.readOnly
      || nextProps.isNextField !== currentProps.isNextField
      || nextProps.formik.isSubmitting !== currentProps.formik.isSubmitting
      || Object.keys(nextProps).length !== Object.keys(currentProps).length
      || getIn(nextProps.formik.values, currentProps.name) !== getIn(currentProps.formik.values, currentProps.name)
      || getIn(nextProps.formik.errors, currentProps.name) !== getIn(currentProps.formik.errors, currentProps.name)
      || getIn(nextProps.formik.touched, currentProps.name) !== getIn(currentProps.formik.touched, currentProps.name);
  };

  return (
    <FastField
      shouldUpdate={ shouldComponentUpdate || defaultShouldComponentUpdate }
      { ...rest }
    >
      { fieldProps => (
        <TimeInput
          ref={ ref }
          validationSchema={ validationSchema }
          as={ as }
          { ...fieldProps }
          { ...rest }
        />
      ) }
    </FastField>
  );
});

TimeInputWrapper.displayName = 'TimeInput';

export default TimeInputWrapper;
